import React, { FC, useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PopoverBody,
  Row,
  Table,
  UncontrolledPopover,
} from 'reactstrap';
import { OptionDataPayload } from '../../../../shared/options';
import { UserReferenceModel } from '../../../../shared/user/base';
import { DeleteTask, ExtendedUser, PendingReviewInfo } from '../../../../shared/user/extended';
import { VolunteerDepartmentModel } from '../../../../shared/volunteer/model';
import {
  ActionButtonModal,
  DateT,
  LinkButton,
  MaterialIcon,
  PermissionBoundary,
  RichMarkdownIt,
} from '../../../components';
import { VerticalStack } from '../../../components/Stacks';
import { JSONForm, PreSubmitData } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import {
  displayName,
  Fetcher,
  renderName,
  TableKVPair,
  useBoolState,
  useFetcher,
} from '../../../utils';
import { downloadJSONData } from '../../../utils/download';
import { captureError } from '../../../utils/errorHandling';
import { DealerEditForm } from '../dealers/dealerEdit';
import { ConfigBooleanSetting } from '../settings/AbstractLiveSettings';
import { UnverifiedEmailIcon } from '../util/email';
import { ProfileEditModal } from './profileEditModal';
import { RegistrationEditModal } from './registrationEditModal';
import { ResetSecondFactorButton } from './resetSecondFactorButton';
import { DealerApplicationStatus, RegistrationAttendanceType } from './utils';

interface UserProfileTabProps {
  readonly userData: ExtendedUser;
  readonly refresh: () => void;
}

interface ResetCodeResponse {
  resetCode: string;
}

export const UserProfileTab: FC<UserProfileTabProps> = ({ userData, refresh }) => {
  return (
    <>
      {userData.deleteTask && (
        <DeleteWarning refresh={refresh} task={userData.deleteTask} userId={userData.id} />
      )}
      <Row>
        <Col className="mb-10" lg={6} xs={12}>
          <UserProfileCard refresh={refresh} user={userData} />
        </Col>
        <Col lg={6} xs={12}>
          <UserRegistrationNew refresh={refresh} user={userData} />
          <PermissionBoundary inline requiredPermissions="registration:cashier">
            <UserActions refresh={refresh} user={userData} />
          </PermissionBoundary>
        </Col>
      </Row>
    </>
  );
};

const DeleteWarning: FC<{
  readonly task: DeleteTask;
  readonly userId: number;
  readonly refresh: () => void;
}> = ({ task, userId, refresh }) => {
  const { ts } = useTranslation();
  const cancelDeletion = useCallback(async () => {
    await api.cancelUserDeleteRequest(userId);
    toast.success(ts('user.deletion.cancel.success'));
    refresh();
  }, [userId, refresh, ts]);

  return (
    <Row>
      <Col lg={12} xs={12}>
        <Alert className="clearfix" color="danger">
          <Row>
            <Col className="align-self-center">
              {ts('user.deletion.notice', task.runAt.toLocaleString())}
            </Col>
            <PermissionBoundary
              inline
              requiredPermissions={['staff:admin:update', 'pii:admin:read', 'pii:admin:delete']}
            >
              <Col className="col-md-auto">
                <div className="float-end">
                  <ActionButtonModal
                    actionContent="user.deletion.cancel.confirmation"
                    buttonContent="user.deletion.cancel.label"
                    color="danger"
                    id="cancelUserDelteModal"
                    onComplete={async () => {
                      await cancelDeletion();
                    }}
                    outline
                    title="user.deletion.cancel.label"
                  >
                    {ts('user.deletion.cancel.prompt')}
                  </ActionButtonModal>
                </div>
              </Col>
            </PermissionBoundary>
          </Row>
        </Alert>
      </Col>
    </Row>
  );
};

const UserActions: FC<{ readonly user: ExtendedUser; readonly refresh: () => void }> = ({
  user,
  refresh,
}) => {
  const [atConResetCode, setAtConResetCode] = useState<string | undefined>();

  const { ts } = useTranslation();
  return (
    <Card id="userActions">
      <CardHeader>{ts('common.actions')}</CardHeader>
      <CardBody>
        <JSONForm<ResetCodeResponse>
          method="patch"
          onSuccess={(data) => {
            setAtConResetCode(data.resetCode);
          }}
          path={`/api/users/${user.id}/reset/atcon`}
        >
          <VerticalStack gap={2}>
            {!user.owner && (
              <Button
                block
                className={atConResetCode && 'complete'}
                color="primary"
                id="atConResetPassword"
                type="submit"
              >
                {atConResetCode ? `Reset code: ${atConResetCode}` : 'At-Con Password Reset'}
              </Button>
            )}
            <PermissionBoundary requiredPermissions="registration:cashier">
              <LinkButton block to={`/housekeeping/cashier/${user.id}`}>
                {ts('open_cashier_interface')}
              </LinkButton>
            </PermissionBoundary>
            <PermissionBoundary requiredPermissions="user:update">
              {!user.verified && !user.owner && (
                <ActionButtonModal
                  actionContent="common.verify"
                  block
                  buttonContent="allow_user_login"
                  color="danger"
                  id="verifyUser"
                  onComplete={async () => {
                    // send api request
                    await api.verifyUserIdentity(user.id, true);
                    toast.success(ts('success_that_user_has_verified_reloading'));
                    refresh();
                  }}
                  title="common.verify_user"
                >
                  <p>{ts('the_user_has_not_yet_verified_their_email')}</p>
                  <p>{ts('are_you_sure_you_want_to_verify_this_user')}</p>
                </ActionButtonModal>
              )}
            </PermissionBoundary>
          </VerticalStack>
        </JSONForm>
        <br />
        {user.hasSecondFactor && <ResetSecondFactorButton refresh={refresh} userId={user.id} />}
      </CardBody>
    </Card>
  );
};

interface UserRegistrationNewProps {
  readonly user: ExtendedUser;
  readonly refresh: () => void;
}

const UserRegistrationNew: FC<UserRegistrationNewProps> = ({ user, refresh }) => {
  const { ts } = useTranslation();

  return (
    <Card className="mb-10">
      <CardHeader>{ts('information')}</CardHeader>
      <CardBody>
        {user.pendingReview ? (
          <>
            <AccountReviewRow refresh={refresh} review={user.matchingBan} userId={user.id} />
            <hr />
          </>
        ) : null}

        <UserRegistrationInformationRow refresh={refresh} user={user} />
        {!user.owner && (
          <>
            <PermissionBoundary inline requiredPermissions="vendor:read">
              <hr />
              <UserDealerInfoRow user={user} />
            </PermissionBoundary>
            <PermissionBoundary inline requiredPermissions="volunteer:read">
              <hr />
              <UserVolunteerInfoRow user={user} />
            </PermissionBoundary>
            {user.hotelEntries && user.hotelEntries.length > 0 && (
              <PermissionBoundary inline requiredPermissions="hotel:view">
                <hr />
                <UserHotelInfoRow user={user} />
              </PermissionBoundary>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

interface AccountReviewRowProps {
  readonly userId: number;
  readonly review?: PendingReviewInfo;
  readonly refresh: () => void;
}

const AccountReviewRow: FC<AccountReviewRowProps> = ({ userId, review, refresh }) => {
  const completeAccountReview = useCallback(async () => {
    await api.completeReview(userId);
    refresh();
  }, [refresh, userId]);

  const { ts } = useTranslation();
  return (
    <div>
      <Row>
        <Col xs={9}>
          <h5>{ts('possible_duplicate_account_with_a')}</h5>
          {review?.firstName && (
            <div id="matchingFirstName">
              <b>{ts('matching_first_name')}</b>: {review.firstName}
            </div>
          )}
          {review?.lastName && (
            <div>
              <b>{ts('matching_last_name')}</b>: {review.lastName}
            </div>
          )}
          {review?.text && <div>{review.text}</div>}
          {!review && <div>{ts('no_matching_ban')}</div>}
        </Col>
        <Col xs={3}>
          <PermissionBoundary inline requiredPermissions="user:note:delete">
            <ActionButtonModal
              actionContent="housekeeping.complete_user_review"
              buttonContent={<MaterialIcon name="check" />}
              className="float-end"
              color="success"
              id="completeReview"
              onComplete={completeAccountReview}
              title="complete_account_review"
            >
              <RichMarkdownIt source={ts('if_this_account_is_a')} />
            </ActionButtonModal>
          </PermissionBoundary>
        </Col>
      </Row>
    </div>
  );
};

interface UserVolunteerInfoRowProps {
  readonly user: ExtendedUser;
}

const UserVolunteerInfoRow: FC<UserVolunteerInfoRowProps> = ({ user }) => {
  const { volunteer } = user;

  const { ts } = useTranslation();
  return (
    <div>
      <Row>
        <Col xs={9}>
          <h5>{ts('volunteer')}</h5>
          {!volunteer && <div>{ts('no_application')}</div>}
          {volunteer && <VolunteerStatus departments={volunteer.departments} />}
        </Col>
        <Col xs={3}>
          <PermissionBoundary inline requiredPermissions="volunteer:update">
            <LinkButton
              className="float-end"
              color="primary"
              id="updateVolunteer"
              outline
              to={`/housekeeping/attendees/user/${user.id}/volunteer`}
            >
              <MaterialIcon name="edit" small />
            </LinkButton>
          </PermissionBoundary>
        </Col>
      </Row>
    </div>
  );
};

const VolunteerStatus: FC<{ readonly departments: VolunteerDepartmentModel[] }> = ({
  departments,
}) => {
  const { ts } = useTranslation();
  const assignment = departments.filter((t) => t.states.includes('assignment'));

  if (assignment.length > 0) {
    return (
      <div>
        {ts('assigned_to')}: {assignment.map((t) => t.name).join(', ')}
      </div>
    );
  }

  const request = departments.filter((t) => t.states.includes('request'));

  if (request.length > 0) {
    return (
      <div>
        {ts('requested')}: {assignment.map((t) => t.name).join(', ')}
      </div>
    );
  }

  return <div>{ts('application_pending')}</div>;
};

interface UserHotelInfoRowProps {
  readonly user: ExtendedUser;
}

const UserHotelInfoRow: FC<UserHotelInfoRowProps> = ({ user }) => {
  const { ts } = useTranslation();
  const empty =
    (!user.hotelEntries || user.hotelEntries.length === 0) &&
    (!user.hotelReservations || user.hotelReservations.length === 0);

  return (
    <div>
      <Row>
        <Col xs={9}>
          <h5>{ts('hotel')}</h5>
          {user.hotelEntries?.map((entry) => {
            const reservation = user.hotelReservations?.find((r) => r.id === entry.reservationId);

            return (
              <div key={entry.entryId}>
                {displayName(entry)}
                {reservation && <> ({ts('has_reservation')})</>}
              </div>
            );
          })}
          {user.hotelReservations?.map((entry) => {
            const reservation = user.hotelEntries?.find((r) => r.reservationId === entry.id);

            if (reservation) {
              return null;
            }

            return (
              <div key={entry.id}>
                {entry.hotelName
                  ? `Reservation for ${entry.hotelName}`
                  : `Reservation #${entry.id}`}
              </div>
            );
          })}
          {empty && <div>{ts('no_application')}</div>}
        </Col>
        {!empty && (
          <Col xs={3}>
            <LinkButton
              className="float-end"
              color="primary"
              id="updateHotel"
              outline
              to={`/housekeeping/hotel/user/${user.id}`}
            >
              <MaterialIcon name="edit" small />
            </LinkButton>
          </Col>
        )}
      </Row>
    </div>
  );
};

interface UserDealerInfoRowProps {
  readonly user: ExtendedUser;
}

const UserDealerInfoRow: FC<UserDealerInfoRowProps> = ({ user }) => {
  const { dealer, dealerAssistance } = user;
  const [createModal, openModelCreate, closeModelCreate] = useBoolState(false);

  const { ts } = useTranslation();
  return (
    <div>
      <Row>
        <Col xs={9}>
          <h5>{ts('dealer')}</h5>
          {!dealer && <div id="noDealerApplication">{ts('no_application')}</div>}
          {dealer && (
            <div>
              <div className="dealerName">{dealer.name}</div>
              {dealer.applications.map((app) => {
                return (
                  <div key={app.area.id}>
                    <b>{ts('area')}:</b> {app.area.name} -{' '}
                    <span className="dealerAppStatus">
                      <DealerApplicationStatus grant={!!app.paidOrderItemId} status={app.status} />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {dealerAssistance.length > 0 && (
            <div>
              {ts('vendor_assistants')}:{' '}
              {dealerAssistance.map((t, id) => {
                return (
                  <span className="dealerAssistant" key={t.dealerId}>
                    <Link to={`/housekeeping/dealers/list/${t.dealerId}`}>{t.name}</Link>
                    {id < dealerAssistance.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </div>
          )}
        </Col>
        <Col xs={3}>
          <PermissionBoundary inline requiredPermissions="vendor:update">
            {dealer && (
              <LinkButton
                className="float-end"
                color="primary"
                id="updateDealer"
                outline
                to={`/housekeeping/dealers/list/${dealer.id}`}
              >
                <MaterialIcon name="edit" small />
              </LinkButton>
            )}
            {!dealer && (
              <Button
                className="float-end"
                color="primary"
                id="createDealer"
                onClick={openModelCreate}
                outline
              >
                <MaterialIcon name="add" small />
              </Button>
            )}
            {createModal && <DealerCreateModal close={closeModelCreate} userId={user.id} />}
          </PermissionBoundary>
        </Col>
      </Row>
    </div>
  );
};

interface DealerCreateModalProps {
  readonly userId: number;
  readonly close: () => void;
}

const DealerCreateModal: FC<DealerCreateModalProps> = ({ userId, close }) => {
  const history = useHistory();
  const [options, setOptions] = useState<OptionDataPayload>({});

  const { ts } = useTranslation();
  const redirectUser = useCallback(
    (result: { id: number }) => {
      toast.success(ts('application_created'));
      history.push(`/housekeeping/dealers/list/${result.id}`);
    },
    [ts, history],
  );

  const preSubmit = useCallback(
    (reqOpts: PreSubmitData<{ options: OptionDataPayload }>) => {
      reqOpts.inputs.options = options;
    },
    [options],
  );

  const dealerFetch = useFetcher(async () => await api.getDealerForm());

  return (
    <Modal className="modal-large" id="createDealerModal" isOpen toggle={close}>
      <ModalHeader toggle={close}>{ts(`dealer.create`)}</ModalHeader>
      {dealerFetch.data ? (
        <JSONForm<{ id: number }, { options: OptionDataPayload }>
          method="post"
          onSuccess={redirectUser}
          path={`/api/users/${userId}/dealer`}
          preSubmit={preSubmit}
        >
          <ModalBody>
            <DealerEditForm
              edit
              form={dealerFetch.data}
              options={options}
              setOptions={setOptions}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" id="submitCreateDealer" type="submit">
              {ts('common.create')}
            </Button>{' '}
            <Button color="secondary" onClick={close} outline>
              {ts('common.cancel')}
            </Button>
          </ModalFooter>
        </JSONForm>
      ) : (
        <Fetcher result={dealerFetch} />
      )}
    </Modal>
  );
};

interface UserRegistrationInformationRowProps {
  readonly user: ExtendedUser;
  readonly refresh: () => void;
}

const UserRegistrationInformationRow: FC<UserRegistrationInformationRowProps> = ({
  user,
  refresh,
}) => {
  const { registration } = user;
  const [edit, enableEdit, disableEdit] = useBoolState(false);

  const { ts } = useTranslation();
  return (
    <div>
      {edit && (
        <RegistrationEditModal
          bannedWords={[]}
          close={disableEdit}
          complete={() => {
            disableEdit();
            refresh();
          }}
          registration={registration ?? undefined}
          userId={user.id}
        />
      )}
      <Row>
        <Col xs={9}>
          <h5>{ts('registration')}</h5>
          {!registration && <div id="notRegistered">{ts('not_registered')}</div>}
          {registration && (
            <div id="registrationInfo">
              {ts('common.status')}: <RegistrationAttendanceType registration={registration} />
            </div>
          )}
          <UserSubAccounts subAccounts={user.subAccounts} />
        </Col>
        <Col xs={3}>
          <PermissionBoundary inline requiredPermissions={['registration:update', 'product:read']}>
            <Button
              className="float-end"
              color={registration ? 'primary' : 'success'}
              id="createReg"
              onClick={enableEdit}
              outline
            >
              <MaterialIcon name="edit" small />
            </Button>
          </PermissionBoundary>
        </Col>
      </Row>
    </div>
  );
};

interface UserSubAccountProps {
  readonly subAccounts: UserReferenceModel[];
}

const UserSubAccounts: FC<UserSubAccountProps> = ({ subAccounts }) => {
  if (subAccounts.length === 0) {
    return null;
  }

  return (
    <>
      <div>Other Accounts: </div>
      <ul>
        {subAccounts.map((subAccount) => {
          return (
            <li key={subAccount.id}>
              <Link to={`/housekeeping/attendees/user/${subAccount.id}`}>
                {renderName(subAccount)}
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
};

interface UserProfileCardProps {
  readonly user: ExtendedUser;
  readonly refresh: () => void;
}

const UserProfileCard: FC<UserProfileCardProps> = ({ user, refresh }) => {
  const [edit, enableEdit, disableEdit] = useBoolState(false);
  const [excludeAuditLogs, enableExclusion, disableExclusion] = useBoolState(false);
  const { ts } = useTranslation();

  const adminDeleteAccount = useCallback(async () => {
    try {
      await api.requestDeleteUser(user.id);
      toast.success(ts('user.deletion.requested'));
    } catch (error) {
      captureError(error as Error);
    }

    refresh();
  }, [refresh, ts, user.id]);

  const exportApiHandler = useCallback(async () => {
    try {
      const result = await api.exportSingleUser(user.id, excludeAuditLogs);
      toast.success(ts('user.export.success'));
      downloadJSONData(result, `user_${user.id}_export`);
    } catch (error) {
      toast.error(ts('user.export.failure'));
      captureError(error as Error);
    }
  }, [user.id, excludeAuditLogs, ts]);

  const exportSingleUser = useCallback(() => {
    // This HTTP request may be long-lived, so we want to make it non-blocking and encapsulate
    // the related handling logic to this separate execution thread.
    // Meanwhile, we let the user know that something *is* indeed happening, and not to leave.
    // This may be better handled as a spinner inside a modal perhaps? Not sure...
    void exportApiHandler();
    toast.info(ts('user.export.do_not_leave'));
  }, [exportApiHandler, ts]);

  return (
    <Card id={`userProfile${user.id}`}>
      {edit && (
        <ProfileEditModal
          close={disableEdit}
          complete={() => {
            refresh();
            disableEdit();
          }}
          showAddress
          user={user}
        />
      )}
      <CardHeader>
        <PermissionBoundary inline requiredPermissions="user:update">
          <ButtonGroup className="float-end">
            <ModalEditButton id="updateProfile" start={enableEdit} />
            <PermissionBoundary inline requiredPermissions={['system:export']}>
              <ActionButtonModal
                actionContent="common.download"
                buttonContent={<MaterialIcon name="download" small />}
                color="success"
                id="singleUserExport"
                onComplete={exportSingleUser}
                style={{ padding: '0px 5px' }}
                title={<>{ts('user.export.modal.title', user.id)}</>}
              >
                {ts('user.export.modal.question', renderName(user))}
                <br />
                <strong>{ts('user.export.modal.warning')}</strong>
                <ConfigBooleanSetting
                  description="user.export.modal.exclude_audits.description"
                  displayName="user.export.modal.exclude_audits.label"
                  name="not_applicable"
                  saving={false}
                  update={(v) => {
                    const func = v ? enableExclusion : disableExclusion;
                    func();
                  }}
                  value={excludeAuditLogs}
                />
              </ActionButtonModal>
            </PermissionBoundary>
            <PermissionBoundary
              inline
              requiredPermissions={['staff:admin:update', 'pii:admin:read', 'pii:admin:delete']}
            >
              {!user.deleteTask && (
                <ActionButtonModal
                  actionContent="common.delete"
                  buttonContent={<MaterialIcon name="delete" small />}
                  color="danger"
                  id="adminDelete"
                  onComplete={adminDeleteAccount}
                  style={{ padding: '0px 5px' }}
                  title="user.deletion.title"
                >
                  {ts('user.deletion.prompt')}
                </ActionButtonModal>
              )}
            </PermissionBoundary>
          </ButtonGroup>
        </PermissionBoundary>
        {ts('user_profile')}
        <IdentityStatus user={user} />
      </CardHeader>
      <CardBody>
        <Table>
          <tbody>
            {user.owner && (
              <tr>
                <th scope="row">{ts('owner_account')}</th>
                <td>
                  <Link to={`/housekeeping/attendees/user/${user.owner.id}`}>
                    {renderName(user.owner)}
                  </Link>
                </td>
              </tr>
            )}
            <TableKVPair displayName="common.first_name" name="firstName" value={user.firstName} />
            <TableKVPair displayName="common.last_name" name="lastName" value={user.lastName} />
            <TableKVPair
              displayName="common.preferred_name"
              name="preferredName"
              value={user.preferredName}
            />
            <TableKVPair displayName="user_name" name="username" value={user.username} />
            <TableKVPair
              displayName="born_at"
              name="bornAt"
              value={
                user.bornAt && (
                  <DateT noTimezoneFix options={{ timeZone: 'UTC' }} value={user.bornAt} />
                )
              }
            />
            <TableKVPair
              displayName="common.email"
              name="email"
              value={user.email && <EmailStatus user={user} />}
            />
            <TableKVPair displayName="common.phone_number" name="phone" value={user.phone} />
            <TableKVPair
              displayName="common.address_line_1"
              name="addressLine1"
              value={user.address?.line1}
            />
            <TableKVPair
              displayName="common.address_line_2_optional"
              name="addressLine2"
              value={user.address?.line2}
            />
            <TableKVPair displayName="common.city" name="addressCity" value={user.address?.city} />
            <TableKVPair
              displayName="common.state_province"
              name="addressState"
              value={user.address?.state}
            />
            <TableKVPair
              displayName="common.zip_postal_code"
              name="addressZipcode"
              value={user.address?.zipcode}
            />
            <TableKVPair
              displayName="common.country"
              name="addressCountry"
              value={user.address?.country}
            />
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

const EmailStatus: FC<{ readonly user: ExtendedUser }> = ({ user }) => {
  const { ts } = useTranslation();
  if (user.pendingEmailChange) {
    return (
      <>
        {user.email}
        <span className="inline-icon" id="pendingEmailChange">
          <MaterialIcon name="warning" type="warning" />
        </span>
        <UncontrolledPopover placement="bottom" target="pendingEmailChange" trigger="hover">
          <PopoverBody>
            <p>{ts('this_user_has_a_pending_email_change_request')}</p>
          </PopoverBody>
        </UncontrolledPopover>
      </>
    );
  }

  if (!user.verified || !user.emailVerified) {
    return (
      <>
        {user.email}
        <UnverifiedEmailIcon userId={user.id} />
      </>
    );
  }

  return user.email;
};

const IdentityStatus: FC<{ readonly user: ExtendedUser }> = ({ user }) => {
  const { ts } = useTranslation();
  if (user.identity?.firstName !== user.firstName) {
    return null;
  }

  return (
    <>
      <span className="inline-icon" id="stripeVerified">
        <MaterialIcon name="verified_user" small type="primary" />
      </span>
      <UncontrolledPopover placement="bottom" target="stripeVerified" trigger="hover">
        <PopoverBody>
          <p>{ts('users_identity_has_been_verified_with_stripe_identity')}</p>
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
};

const ModalEditButton: FC<{ readonly id: string; readonly start: () => void }> = ({
  id,
  start,
}) => {
  return (
    <Button
      className="float-end"
      color="primary"
      id={id}
      onClick={start}
      style={{ padding: '0px 5px' }}
    >
      <MaterialIcon name="edit" small />
    </Button>
  );
};
